import { LitElement } from "lit"
import type mapboxgl from "mapbox-gl"
import { isMapControl, type MapControl, type MapControlPosition } from "./map-control.ts"
import { MAP_EVENT, mapEvent } from "../map/types.ts"

// deno-lint-ignore no-explicit-any
type Constructor<T = object> = new (...args: any[]) => T

interface PendingControl {
  control: MapControl
  position?: MapControlPosition
}

/**
 * Mixin that adds MapControl plugin API to a LitElement-based map component.
 *
 * Usage:
 *   class TripMap extends MapControlHost(LitElement) { ... }
 *
 * Provides:
 *   - addControl(control, position?) — queues if map not ready, otherwise delegates to map.addControl
 *   - removeControl(control) — delegates to map.removeControl
 *   - _flushAndReady() — call after map `load` fires: flushes queue, scans children, dispatches map-ready
 *   - _teardownControlHost() — call in disconnectedCallback
 */
export function MapControlHost<T extends Constructor<LitElement>>(Base: T) {
  return class extends Base {
    protected _map: mapboxgl.Map | null = null
    /**
     * Latched at the same moment `map-ready` fires, and read by
     * `lib/map/bridge.ts` — an island that hydrates after the event has already
     * been dispatched has no event left to wait for, and this is what it asks
     * instead.
     */
    mapReady = false
    private _pendingControls: PendingControl[] = []
    private _observer: MutationObserver | null = null

    addControl(control: MapControl, position?: MapControlPosition): void {
      if (this._map) {
        this._map.addControl(
          control as unknown as mapboxgl.IControl,
          position,
        )
      } else {
        this._pendingControls.push({ control, position })
      }
    }

    removeControl(control: MapControl): void {
      // Remove from pending queue if not yet added
      const idx = this._pendingControls.findIndex((p) => p.control === control)
      if (idx !== -1) {
        this._pendingControls.splice(idx, 1)
        return
      }
      if (this._map) {
        try {
          this._map.removeControl(control as unknown as mapboxgl.IControl)
        } catch {
          // control may not be added — ignore
        }
      }
    }

    /**
     * Call this after the Mapbox map `load` event fires.
     * Flushes queued controls, scans direct children for MapControl elements,
     * sets up MutationObserver, and dispatches `map-ready`.
     */
    protected _flushAndReady(): void {
      if (!this._map) return

      // Flush pending controls added before map was ready
      for (const { control, position } of this._pendingControls) {
        this._map.addControl(
          control as unknown as mapboxgl.IControl,
          position,
        )
      }
      this._pendingControls = []

      // Scan existing direct children for MapControl implementors
      for (const child of Array.from(this.children)) {
        if (isMapControl(child)) {
          this._map.addControl(
            child as unknown as mapboxgl.IControl,
            child.position,
          )
        }
      }

      // Observe future child additions/removals
      this._observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node instanceof Element && isMapControl(node)) {
              this.addControl(node, node.position)
            }
          }
          for (const node of mutation.removedNodes) {
            if (node instanceof Element && isMapControl(node)) {
              this.removeControl(node)
            }
          }
        }
      })
      this._observer.observe(this, { childList: true })

      this.mapReady = true
      this.dispatchEvent(mapEvent(MAP_EVENT.mapReady, { map: this._map }))
    }

    protected _teardownControlHost(): void {
      this.mapReady = false
      this._observer?.disconnect()
      this._observer = null
      this._pendingControls = []
    }

    override disconnectedCallback(): void {
      super.disconnectedCallback()
      this._teardownControlHost()
    }
  }
}
